import { useLayoutEffect } from 'react'
import type { ComponentType } from 'react'

/**
 * Pins the page to the legacy "Voltage" palette for as long as it is mounted.
 *
 * The frozen fantasy screens and the admin panel carry ~10,700 lines of CSS
 * baked for a near-black ground — black `rgba()` shadows, hardcoded hex, glow.
 * Rather than port CSS that is on its way out, those routes render this and
 * keep the exact look they shipped with, in either theme. See the
 * `[data-skin="voltage"]` block in `shared/styles/tokens.css`.
 *
 * The attribute goes on `<html>` rather than a wrapper element because the page
 * ground itself (`html`/`body` background) has to change too — a wrapper would
 * leave a light gutter around dark content.
 *
 * Mounts are reference-counted: on navigation React can mount the incoming
 * route before the outgoing one's cleanup runs, and a naive remove-on-unmount
 * would strip the attribute the new screen just set.
 *
 * Delete this component, its token block and every call site when classic
 * fantasy is removed.
 */

let mountCount = 0

export function LegacySkin() {
  useLayoutEffect(() => {
    mountCount += 1
    document.documentElement.setAttribute('data-skin', 'voltage')

    return () => {
      mountCount -= 1
      if (mountCount === 0) {
        document.documentElement.removeAttribute('data-skin')
      }
    }
  }, [])

  return null
}

/**
 * Route-level wrapper: `component: withLegacySkin(LigasPage)`. Keeps the pin at
 * the route definition instead of threading `<LegacySkin />` through every
 * early return inside a page component.
 */
export function withLegacySkin<P extends object>(Component: ComponentType<P>) {
  return function LegacySkinned(props: P) {
    return (
      <>
        <LegacySkin />
        <Component {...props} />
      </>
    )
  }
}
